import { MAX_PUSH_BODY_BYTES, MAX_PUSH_RECORDS, PULL_PAGE_SIZE, REQUEST_TIMEOUT_MS } from './constants';
import type { EncryptedRecord } from './crypto';
import { ServerErrorCode, SyncHttpError, SyncNetworkError, SyncProtocolError } from './errors';

export interface PushResult {
  /** Records the server stored (new, or newer than its copy). */
  readonly accepted: readonly string[];
  /** Records the server already held in the same or a newer version. */
  readonly stale: readonly string[];
}

export interface PulledRecord extends EncryptedRecord {
  /** The server's per-space sequence number of this version. */
  readonly seq: number;
}

export interface PullPage {
  /** Identifier of the incarnation of the space that answered (see `SyncConfig.epoch`). */
  readonly epoch: string;
  readonly records: readonly PulledRecord[];
  /** Highest sequence number in this page (the request's cursor when the page is empty). */
  readonly cursor: number;
  readonly hasMore: boolean;
}

/** What the sync engine needs from the server; `HttpTransport` is the real implementation. */
export interface SyncTransport {
  /** Create the space (first caller sets the token) or verify the token of an existing one. */
  putSpace(): Promise<{ readonly created: boolean; readonly epoch: string }>;
  push(records: readonly EncryptedRecord[]): Promise<PushResult>;
  pull(cursor: number, limit?: number): Promise<PullPage>;
  /** Delete the space and every record in it. */
  deleteSpace(): Promise<void>;
}

export interface HttpTransportOptions {
  /** Server origin such as `https://sync.example.com`; empty for the page's own origin. */
  readonly baseUrl: string;
  readonly spaceId: string;
  /** The write token. Sent only in the Authorization header; never logged or put into errors. */
  readonly authToken: string;
  readonly fetch?: typeof fetch;
  readonly timeoutMs?: number;
}

export interface ChunkLimits {
  readonly maxRecords: number;
  readonly maxBodyBytes: number;
}

const DEFAULT_CHUNK_LIMITS: ChunkLimits = { maxRecords: MAX_PUSH_RECORDS, maxBodyBytes: MAX_PUSH_BODY_BYTES };
/** Bytes of JSON framing around the records: `{"records":[]}`. */
const BODY_FRAMING_BYTES = 16;
const MAX_ERROR_MESSAGE_CHARS = 200;

/** Split records into push requests of at most `maxRecords` records and about `maxBodyBytes` of JSON. */
export function chunkRecords(
  records: readonly EncryptedRecord[],
  limits: ChunkLimits = DEFAULT_CHUNK_LIMITS,
): EncryptedRecord[][] {
  const chunks: EncryptedRecord[][] = [];
  let current: EncryptedRecord[] = [];
  let currentBytes = BODY_FRAMING_BYTES;
  for (const record of records) {
    // Every character is ASCII (hex, base64, digits), so length equals the UTF-8 byte count.
    const size = JSON.stringify(record).length + 1;
    if (
      current.length > 0 &&
      (current.length >= limits.maxRecords || currentBytes + size > limits.maxBodyBytes)
    ) {
      chunks.push(current);
      current = [];
      currentBytes = BODY_FRAMING_BYTES;
    }
    current.push(record);
    currentBytes += size;
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}

/** `Retry-After` in seconds from its integer or HTTP-date form; null when absent or unusable. */
export function parseRetryAfter(value: string | null, nowMs: number): number | null {
  if (value === null) return null;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const date = Date.parse(trimmed);
  if (Number.isNaN(date)) return null;
  return Math.max(0, Math.ceil((date - nowMs) / 1000));
}

const knownCodes: ReadonlySet<string> = new Set(Object.values(ServerErrorCode));

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringArray(value: unknown, what: string): string[] {
  if (!Array.isArray(value) || !value.every((item): item is string => typeof item === 'string')) {
    throw new SyncProtocolError(`Malformed ${what} in the server response`);
  }
  return value;
}

function parsePushResult(body: unknown): PushResult {
  if (!isRecord(body)) throw new SyncProtocolError('Malformed push response');
  return {
    accepted: stringArray(body.accepted, 'accepted list'),
    stale: stringArray(body.stale, 'stale list'),
  };
}

function parsePulledRecord(value: unknown): PulledRecord {
  if (
    !isRecord(value) ||
    typeof value.rid !== 'string' ||
    typeof value.hlc !== 'string' ||
    typeof value.ciphertext !== 'string' ||
    typeof value.seq !== 'number'
  ) {
    throw new SyncProtocolError('Malformed record in the pull response');
  }
  return { rid: value.rid, hlc: value.hlc, ciphertext: value.ciphertext, seq: value.seq };
}

function parseEpoch(value: unknown): string {
  if (typeof value !== 'string' || value === '') {
    throw new SyncProtocolError('The server response carries no space epoch');
  }
  return value;
}

function parsePullPage(body: unknown): PullPage {
  if (
    !isRecord(body) ||
    !Array.isArray(body.records) ||
    typeof body.cursor !== 'number' ||
    typeof body.hasMore !== 'boolean'
  ) {
    throw new SyncProtocolError('Malformed pull response');
  }
  return {
    epoch: parseEpoch(body.epoch),
    records: body.records.map(parsePulledRecord),
    cursor: body.cursor,
    hasMore: body.hasMore,
  };
}

function errorFromResponse(status: number, bodyText: string, retryAfter: number | null): SyncHttpError {
  let code: ServerErrorCode = ServerErrorCode.Unknown;
  let message = `Request failed with status ${status}`;
  try {
    const body: unknown = JSON.parse(bodyText);
    if (isRecord(body)) {
      if (typeof body.error === 'string' && knownCodes.has(body.error)) code = body.error as ServerErrorCode;
      if (typeof body.message === 'string') message = body.message.slice(0, MAX_ERROR_MESSAGE_CHARS);
    }
  } catch {
    // Not JSON (a proxy's error page, for example): keep the generic message.
  }
  return new SyncHttpError(status, code, message, retryAfter);
}

/** Strip trailing slashes; an empty base URL addresses the page's own origin. */
export function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.trim().replace(/\/+$/, '');
}

/** HTTP client of the sync protocol for one space (architecture.md section 6.2). */
export class HttpTransport implements SyncTransport {
  private readonly base: string;
  private readonly spaceUrl: string;
  private readonly authToken: string;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;

  constructor(options: HttpTransportOptions) {
    this.base = normalizeBaseUrl(options.baseUrl);
    this.spaceUrl = `${this.base}/api/v1/spaces/${options.spaceId}`;
    this.authToken = options.authToken;
    this.fetchImpl = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.timeoutMs = options.timeoutMs ?? REQUEST_TIMEOUT_MS;
  }

  async putSpace(): Promise<{ created: boolean; epoch: string }> {
    const response = await this.request('PUT', this.spaceUrl);
    const body = parseJson(response.text);
    const created =
      isRecord(body) && typeof body.created === 'boolean' ? body.created : response.status === 201;
    return { created, epoch: parseEpoch(isRecord(body) ? body.epoch : undefined) };
  }

  /** Push records; more than one request's worth is split into several requests, in order. */
  async push(records: readonly EncryptedRecord[]): Promise<PushResult> {
    const accepted: string[] = [];
    const stale: string[] = [];
    for (const chunk of chunkRecords(records)) {
      const response = await this.request(
        'POST',
        `${this.spaceUrl}/push`,
        JSON.stringify({ records: chunk }),
      );
      const result = parsePushResult(parseJson(response.text));
      accepted.push(...result.accepted);
      stale.push(...result.stale);
    }
    return { accepted, stale };
  }

  async pull(cursor: number, limit: number = PULL_PAGE_SIZE): Promise<PullPage> {
    const response = await this.request('GET', `${this.spaceUrl}/pull?cursor=${cursor}&limit=${limit}`);
    return parsePullPage(parseJson(response.text));
  }

  async deleteSpace(): Promise<void> {
    await this.request('DELETE', this.spaceUrl);
  }

  /** Whether the server answers its health check; never throws. */
  async health(): Promise<boolean> {
    return checkServerHealth(this.base, this.fetchImpl, this.timeoutMs);
  }

  private async request(
    method: string,
    url: string,
    body?: string,
  ): Promise<{ status: number; text: string }> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${this.authToken}`,
      'X-Request-ID': globalThis.crypto.randomUUID(),
    };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    return sendRequest(
      this.fetchImpl,
      url,
      { method, headers, ...(body === undefined ? {} : { body }) },
      this.timeoutMs,
    );
  }
}

export function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    throw new SyncProtocolError('The server response is not valid JSON');
  }
}

export async function sendRequest(
  fetchImpl: typeof fetch,
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<{ status: number; text: string }> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    const response = await fetchImpl(url, {
      ...init,
      signal: controller.signal,
      cache: 'no-store',
      credentials: 'omit',
    });
    const text = await response.text();
    if (!response.ok) {
      throw errorFromResponse(
        response.status,
        text,
        parseRetryAfter(response.headers.get('Retry-After'), Date.now()),
      );
    }
    return { status: response.status, text };
  } catch (error) {
    if (error instanceof SyncHttpError || error instanceof SyncProtocolError) throw error;
    throw new SyncNetworkError(timedOut ? 'The request timed out' : 'The server is unreachable', timedOut);
  } finally {
    clearTimeout(timer);
  }
}

/** Probe `GET /api/health` of a server; false on any failure. For the settings screen's connection test. */
export async function checkServerHealth(
  baseUrl: string,
  fetchImpl: typeof fetch = (input, init) => globalThis.fetch(input, init),
  timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<boolean> {
  try {
    const response = await sendRequest(
      fetchImpl,
      `${normalizeBaseUrl(baseUrl)}/api/health`,
      { method: 'GET' },
      timeoutMs,
    );
    const body = parseJson(response.text);
    return isRecord(body) && body.status === 'ok';
  } catch {
    return false;
  }
}
